import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileText, Play, Image, Rocket, Globe, X, Mail } from "lucide-react";
import { toast } from "react-toastify";
import {
  getMeetingRoomById,
  updateMeetingRoom,
} from "../../../../services/roomServices";
import logger from '../../../../utils/logger';
import userService from "../../../../services/userServices";
import { useSiteContext } from "../../../../context/SiteContext";

function EditRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useSiteContext();
  const fileInputRef = useRef(null);
  const passwordInputRefs = useRef([null, null, null, null]);
  const [meetingName, setMeetingName] = useState("");
  const [roomSettings, setRoomSettings] = useState({
    allowChat: true,
    allowScreenSharing: false,
    muteParticipantsOnEntry: false,
    allowReactions: true,
    allowRaiseHand: true,
    allowParticipantRanking: false,
    guestCanUnmute: false,
    requirePassword: false,
    password: "",
    hostApprovalRequired: false,
    saveAsPreset: false,
    presetName: "",
    // exitUrlEnabled: false,
    // exitUrl: "",
  });
  const [elementPresets, setElementPresets] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const [exitUrlEnabled, setExitUrlEnabled] = useState(false);
  const [exitUrl, setExitUrl] = useState("");

  const [customButtonEnabled, setCustomButtonEnabled] = useState(false);
  const [customButtonUrl, setCustomButtonUrl] = useState("");
  const [customButtonText, setCustomButtonText] = useState("");

  const [waitingRoomTextEnabled, setWaitingRoomTextEnabled] = useState(false);
  const [waitingRoomTitle, setWaitingRoomTitle] = useState("");
  const [waitingRoomMessage, setWaitingRoomMessage] = useState("");
  const [waitingRoomSubText, setWaitingRoomSubText] = useState("");

  const [customBackgroundEnabled, setCustomBackgroundEnabled] = useState(false);
  const [backgroundType, setBackgroundType] = useState("solid");
  const [backgroundColor, setBackgroundColor] = useState("#1a1a2e");
  const [gradientColor1, setGradientColor1] = useState("#1a1a2e");
  const [gradientColor2, setGradientColor2] = useState("#615fff");
  const [gradientDirection, setGradientDirection] = useState("to right");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);

  // Co-Host state
  const [invitesEmailInput, setInvitesEmailInput] = useState("");
  const [invitesList, setInvitesList] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [inviteEmails, setInviteEmails] = useState([]);

  // Speakers state
  const [speakersEmailInput, setSpeakersEmailInput] = useState("");
  const [speakersList, setSpeakersList] = useState([]);
  const [showSpeakerSuggestions, setShowSpeakerSuggestions] = useState(false);
  const [selectedSpeakerSuggestionIndex, setSelectedSpeakerSuggestionIndex] =
    useState(-1);
  const [speakerSuggestions, setSpeakerSuggestions] = useState([]);
  const [checkingSpeakerUser, setCheckingSpeakerUser] = useState(false);
  const [speakerUserExists, setSpeakerUserExists] = useState(null);
  const [speakerInviteEmails, setSpeakerInviteEmails] = useState([]);

  // Common email domains for suggestions
  const commonDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "aol.com",
    "mail.com",
    "protonmail.com",
  ];

  // Helper function to validate URL
  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (roomId) {
      getMeetingRoomById(roomId)
        .then((response) => {
          const roomData = response.data;
          logger("log", "Full room data:", roomData);
          setMeetingName(roomData.roomName || "");
          setRoomSettings({
            allowChat: roomData.allowChat ?? true,
            allowScreenSharing: roomData.allowScreenSharing ?? false,
            muteParticipantsOnEntry: roomData.muteParticipants ?? false,
            allowReactions: roomData.allowReactions ?? true,
            allowRaiseHand: roomData.allowRaiseHand ?? true,
            allowParticipantRanking: roomData.allowParticipantRanking ?? false,
            guestCanUnmute: roomData.guestCanUnmute ?? false,
            requirePassword: roomData.requirePassword ?? false,
            password: roomData.password || "",
            hostApprovalRequired: roomData.hostApprovalRequired ?? false,
            saveAsPreset: false, // Not relevant for editing
            presetName: "",
            // exitUrlEnabled: roomData.exitUrlEnabled ?? false,
            // exitUrl: roomData.exitUrl ?? "",
          });
          setElementPresets(roomData.presets || []);

          setExitUrlEnabled(roomData.exitUrlEnabled ?? false);
          setExitUrl(roomData.exitUrl ?? "");

          // Load emails (co-hosts) - handle both array and JSON string formats
          // Check multiple possible field names
          let emailsData =
            roomData.emails || roomData.coHosts || roomData.coHost || [];
          if (typeof emailsData === "string" && emailsData.trim()) {
            try {
              emailsData = JSON.parse(emailsData);
            } catch (e) {
              logger("error", "Error parsing emails:", e);
              emailsData = [];
            }
          }
          setInvitesList(Array.isArray(emailsData) ? emailsData : []);

          // Load speakers - handle both array and JSON string formats
          let speakersData = roomData.speakers || [];
          if (typeof speakersData === "string" && speakersData.trim()) {
            try {
              speakersData = JSON.parse(speakersData);
            } catch (e) {
              logger("error", "Error parsing speakers:", e);
              speakersData = [];
            }
          }
          setSpeakersList(Array.isArray(speakersData) ? speakersData : []);

          logger("log", 
            "Loaded emails (co-hosts):",
            emailsData,
            "speakers:",
            speakersData
          );

          // const exitUrlValue = roomData.exitUrl;
          // const hasExitUrl =
          //   exitUrlValue &&
          //   exitUrlValue !== "false" &&
          //   exitUrlValue !== false &&
          //   exitUrlValue.trim() !== "";
          // setExitUrlEnabled(roomData.exitUrlEnabled ?? hasExitUrl);
          // setExitUrl(hasExitUrl ? exitUrlValue : "");

          // Custom Button - Enable if both text and URL exist or if explicitly enabled
          // const hasCustomButton =
          //   roomData.customButtonText &&
          //   roomData.customButtonText.trim() !== "" &&
          //   roomData.customButtonUrl &&
          //   roomData.customButtonUrl.trim() !== "";
          // setCustomButtonEnabled(
          //   roomData.customButtonEnabled ?? hasCustomButton
          // );
          setCustomButtonEnabled(roomData.customButtonEnabled ?? false);
          setCustomButtonText(roomData.customButtonText || "");
          setCustomButtonUrl(roomData.customButtonUrl || "");

          // Waiting Room Text - Enable if any text field has data or if explicitly enabled
          const hasWaitingRoomText =
            (roomData.waitingRoomTitle &&
              roomData.waitingRoomTitle.trim() !== "") ||
            (roomData.waitingRoomMessage &&
              roomData.waitingRoomMessage.trim() !== "") ||
            (roomData.waitingRoomSubText &&
              roomData.waitingRoomSubText.trim() !== "");
          setWaitingRoomTextEnabled(
            roomData.waitingRoomTextEnabled ?? hasWaitingRoomText
          );
          setWaitingRoomTitle(roomData.waitingRoomTitle || "");
          setWaitingRoomMessage(roomData.waitingRoomMessage || "");
          setWaitingRoomSubText(roomData.waitingRoomSubText || "");

          // Custom Background - Enable if there's actual custom data
          const backgroundImageUrl =
            roomData.meetingBackgroundImage || roomData.backgroundImageUrl;
          const hasBackgroundImage =
            backgroundImageUrl && backgroundImageUrl.trim() !== "";
          const hasCustomColor =
            roomData.backgroundColor &&
            roomData.backgroundColor !== "" &&
            roomData.backgroundColor !== "#1a1a2e";
          const hasCustomGradient =
            roomData.meetingBackgroundGradient ||
            (roomData.gradientColor1 &&
              roomData.gradientColor1 !== "" &&
              roomData.gradientColor1 !== "#1a1a2e") ||
            (roomData.gradientColor2 &&
              roomData.gradientColor2 !== "" &&
              roomData.gradientColor2 !== "#615fff");

          const hasCustomBackground =
            hasBackgroundImage || hasCustomColor || hasCustomGradient;

          setCustomBackgroundEnabled(
            roomData.customBackgroundEnabled ?? hasCustomBackground
          );

          // Auto-detect background type based on data: image first, then gradient, then solid
          let detectedBackgroundType = "solid";
          if (hasBackgroundImage) {
            detectedBackgroundType = "image";
          } else if (hasCustomGradient) {
            detectedBackgroundType = "gradient";
          }

          const meetingBackgroundGradient = roomData.meetingBackgroundGradient ? JSON.parse(roomData.meetingBackgroundGradient) : null;
          const gradientColor1 = meetingBackgroundGradient ? meetingBackgroundGradient.color1 : "";
          const gradientColor2 = meetingBackgroundGradient ? meetingBackgroundGradient.color2 : "";
          const gradientDirection = meetingBackgroundGradient ? meetingBackgroundGradient.direction : "";

          setBackgroundType(roomData.meetingBackgroundType || detectedBackgroundType);
          setBackgroundColor(roomData.meetingBackgroundSolid);
          setGradientColor1(gradientColor1);
          setGradientColor2(gradientColor2);
          setGradientDirection(gradientDirection);

          if (hasBackgroundImage) {
            setBackgroundImagePreview(backgroundImageUrl);
          }
        })
        .catch((error) => {
          logger("error", "Error fetching room data:", error);
          toast.error("Failed to load room data");
        });
    }
  }, [roomId]);

  const handleSettingChange = (setting, value) => {
    setRoomSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  // Handle password digit input
  const handlePasswordDigitChange = (index, value) => {
    // Only allow numbers
    const digit = value.replace(/\D/g, "");
    if (digit.length > 1) return; // Only allow single digit

    const currentPassword = roomSettings.password.split("");
    currentPassword[index] = digit;
    const newPassword = currentPassword.join("").slice(0, 4);
    
    handleSettingChange("password", newPassword);

    // Auto-focus next input if digit entered
    if (digit && index < 3 && passwordInputRefs.current[index + 1]) {
      passwordInputRefs.current[index + 1].focus();
    }
  };

  // Handle password backspace
  const handlePasswordKeyDown = (index, e) => {
    if (e.key === "Backspace" && !roomSettings.password[index] && index > 0) {
      // If current field is empty and backspace is pressed, focus previous field
      passwordInputRefs.current[index - 1].focus();
    }
  };

  // Handle paste for password
  const handlePasswordPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pastedData) {
      handleSettingChange("password", pastedData);
      // Focus the next empty field or the last field
      const nextIndex = Math.min(pastedData.length, 3);
      if (passwordInputRefs.current[nextIndex]) {
        passwordInputRefs.current[nextIndex].focus();
      }
    }
  };

  // Generate suggestions based on input (Co-Host)
  useEffect(() => {
    const input = invitesEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!invitesList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!invitesList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!invitesList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    if (emailRegex.test(input)) {
      setSuggestions(newSuggestions.slice(0, 5));
    } else {
      setSuggestions(newSuggestions.slice(0, 5));
    }

    if (newSuggestions.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }

    setSelectedSuggestionIndex(-1);
  }, [invitesEmailInput, invitesList]);

  // Check if user exists when valid email is entered (Co-Host)
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = invitesEmailInput.trim();

    if (input.length === 0) {
      setUserExists(null);
      setCheckingUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setUserExists(false);
          } else {
            setUserExists(false);
            logger("error", "Error checking user:", error);
          }
        } finally {
          setCheckingUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setUserExists(null);
    }
  }, [invitesEmailInput]);

  // Generate speaker suggestions based on input
  useEffect(() => {
    const input = speakersEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSpeakerSuggestions([]);
      setShowSpeakerSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!speakersList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!speakersList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!speakersList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    if (emailRegex.test(input)) {
      setSpeakerSuggestions(newSuggestions.slice(0, 5));
    } else {
      setSpeakerSuggestions(newSuggestions.slice(0, 5));
    }

    if (newSuggestions.length > 0) {
      setShowSpeakerSuggestions(true);
    } else {
      setShowSpeakerSuggestions(false);
    }

    setSelectedSpeakerSuggestionIndex(-1);
  }, [speakersEmailInput, speakersList]);

  // Check if speaker user exists when valid email is entered
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = speakersEmailInput.trim();

    if (input.length === 0) {
      setSpeakerUserExists(null);
      setCheckingSpeakerUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingSpeakerUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setSpeakerUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setSpeakerUserExists(false);
          } else {
            setSpeakerUserExists(false);
            logger("error", "Error checking speaker user:", error);
          }
        } finally {
          setCheckingSpeakerUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setSpeakerUserExists(null);
    }
  }, [speakersEmailInput]);

  // Handle adding co-host (existing user or invite)
  const handleAddCoHost = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // If isInvite is null, we need to check if user exists
    if (isInvite === null) {
      try {
        setCheckingUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          isInvite = true;
          logger("error", "Error checking user:", error);
        }
      } finally {
        setCheckingUser(false);
      }
    }

    if (!invitesList.includes(trimmedEmail)) {
      setInvitesList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setInvitesEmailInput("");
      setShowSuggestions(false);
      setUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Co-host added");
    } else {
      toast.warning("Email already added.");
    }
  };

  // Handle adding speaker (existing user or invite)
  const handleAddSpeaker = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // If isInvite is null, we need to check if user exists
    if (isInvite === null) {
      try {
        setCheckingSpeakerUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          isInvite = true;
          logger("error", "Error checking speaker user:", error);
        }
      } finally {
        setCheckingSpeakerUser(false);
      }
    }

    if (!speakersList.includes(trimmedEmail)) {
      setSpeakersList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setSpeakerInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setSpeakersEmailInput("");
      setShowSpeakerSuggestions(false);
      setSpeakerUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Speaker added");
    } else {
      toast.warning("Email already added.");
    }
  };

  const removeElementPreset = (id) => {
    setElementPresets((prev) => prev.filter((preset) => preset.id !== id));
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, dropIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(dropIndex);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    const newPresets = [...elementPresets];
    const draggedPreset = newPresets[draggedItem];

    // Remove the dragged item from its original position
    newPresets.splice(draggedItem, 1);

    // Insert it at the new position
    newPresets.splice(dropIndex, 0, draggedPreset);

    setElementPresets(newPresets);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const addElementPreset = () => {
    // TODO: Implement add preset modal functionality
    logger("log", "Add preset clicked");
  };

  const getWidgetIcon = (type, colorcss = "text-black") => {
    switch (type) {
      case "document":
        return <FileText size={16} className={colorcss} />;
      case "video":
        return <Play size={16} className={colorcss} />;
      case "image":
        return <Image size={16} className={colorcss} />;
      case "cta":
        return <Rocket size={16} className={colorcss} />;
      case "website":
        return <Globe size={16} className={colorcss} />;
      default:
        return <FileText size={16} className={colorcss} />;
    }
  };

  const handleUpdateMeeting = async () => {
    // Validation
    if (!meetingName.trim()) {
      toast.error("Please enter a meeting name");
      return;
    }

    if (meetingName.trim().length < 3) {
      toast.error("Meeting name must be at least 3 characters long");
      return;
    }

    // Check if password is required but not provided or invalid
    if (roomSettings.requirePassword) {
      if (!roomSettings.password.trim()) {
        toast.error("Please enter a password for the meeting");
        return;
      }
      if (roomSettings.password.length !== 4 || !/^\d{4}$/.test(roomSettings.password)) {
        toast.error("Password must be exactly 4 digits");
        return;
      }
    }

    // Validate exit URL if enabled
    if (exitUrlEnabled) {
      const exitUrlStr = typeof exitUrl === "string" ? exitUrl : "";
      if (!exitUrlStr.trim()) {
        toast.error("Please enter an exit URL");
        return;
      }
      if (!isValidUrl(exitUrlStr.trim())) {
        toast.error("Please enter a valid URL (e.g., https://example.com)");
        return;
      }
    }

    // Validate custom button if enabled
    if (customButtonEnabled) {
      if (!customButtonText.trim()) {
        toast.error("Please enter a button name");
        return;
      }
      if (customButtonText.trim().length < 3) {
        toast.error("Button name must be at least 3 characters long");
        return;
      }
      if (!customButtonUrl.trim()) {
        toast.error("Please enter a button URL");
        return;
      }
      if (!isValidUrl(customButtonUrl.trim())) {
        toast.error(
          "Please enter a valid button URL (e.g., https://example.com)"
        );
        return;
      }
    }

    // Validate waiting room text if enabled
    if (waitingRoomTextEnabled) {
      if (!waitingRoomTitle.trim()) {
        toast.error("Please enter waiting room title");
        return;
      }
      if (waitingRoomTitle.trim().length < 3) {
        toast.error("Waiting room title must be at least 3 characters long");
        return;
      }
      if (!waitingRoomMessage.trim()) {
        toast.error("Please enter waiting room message");
        return;
      }
      if (waitingRoomMessage.trim().length < 3) {
        toast.error("Waiting room message must be at least 3 characters long");
        return;
      }
      if (!waitingRoomSubText.trim()) {
        toast.error("Please enter waiting room sub text");
        return;
      }
      if (waitingRoomSubText.trim().length < 3) {
        toast.error("Waiting room sub text must be at least 3 characters long");
        return;
      }
    }

    // Validate background settings only if custom background is enabled
    if (customBackgroundEnabled) {
      if (
        backgroundType === "image" &&
        !backgroundImage &&
        !backgroundImagePreview
      ) {
        toast.error("Please select a background image");
        return;
      }
      if (backgroundType === "image" && backgroundImage) {
        const maxSize = 5 * 1024 * 1024;
        if (backgroundImage.size > maxSize) {
          toast.error("Background image size should not exceed 5MB");
          return;
        }
      }
      if (backgroundType === "gradient") {
        if (!gradientColor1 || !gradientColor2) {
          toast.error("Please select both gradient colors");
          return;
        }
      }
      if (backgroundType === "solid" && !backgroundColor) {
        toast.error("Please select a background color");
        return;
      }
    }

    try {
      const formData = new FormData();

      formData.append("roomName", meetingName.trim());
      formData.append("roomType", "Meeting");

      // Add room settings
      Object.keys(roomSettings).forEach((key) => {
        const value = roomSettings[key];
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      // Add presets
      if (elementPresets.length > 0) {
        formData.append("presets", JSON.stringify(elementPresets));
      }

      // Add emails as JSON string
      if (invitesList.length > 0) {
        formData.append("emails", JSON.stringify(invitesList));
      }

      // Add speakers as JSON string
      if (speakersList.length > 0) {
        formData.append("speakers", JSON.stringify(speakersList));
      }

      // Add exit URL enabled settings
      formData.append("exitUrlEnabled", exitUrlEnabled);
      if (exitUrlEnabled) {
        formData.append("exitUrl", exitUrl.trim());
      } else {
        formData.append("exitUrl", "");
      }

      // Add exit URL settings
      // const exitUrlStr = typeof exitUrl === "string" ? exitUrl : "";
      // if (exitUrlEnabled && exitUrlStr.trim()) {
      //   formData.append("exitUrl", exitUrlStr.trim());
      // } else {
      //   formData.append("exitUrl", "");
      // }

      // Add custom button settings
      formData.append("customButtonEnabled", customButtonEnabled);
      if (customButtonEnabled) {
        formData.append("customButtonText", customButtonText.trim());
        formData.append("customButtonUrl", customButtonUrl.trim());
      } else {
        formData.append("customButtonText", "");
        formData.append("customButtonUrl", "");
      }

      // Add waiting room text settings
      formData.append("waitingRoomTextEnabled", waitingRoomTextEnabled);
      if (waitingRoomTextEnabled) {
        formData.append("waitingRoomTitle", waitingRoomTitle.trim());
        formData.append("waitingRoomMessage", waitingRoomMessage.trim());
        formData.append("waitingRoomSubText", waitingRoomSubText.trim());
      } else {
        formData.append("waitingRoomTitle", "");
        formData.append("waitingRoomMessage", "");
        formData.append("waitingRoomSubText", "");
      }

      // Add custom background settings
      formData.append("customBackgroundEnabled", customBackgroundEnabled);
      if (customBackgroundEnabled) {
        formData.append("backgroundType", backgroundType);

        if (backgroundType === "solid") {
          formData.append("backgroundColor", backgroundColor);
        } else if (backgroundType === "gradient") {
          formData.append("gradientColor1", gradientColor1);
          formData.append("gradientColor2", gradientColor2);
          formData.append("gradientDirection", gradientDirection);
        } else if (backgroundType === "image" && backgroundImage) {
          formData.append("backgroundImage", backgroundImage);
        }
      }

      await updateMeetingRoom(roomId, formData);
      toast.success("Room updated successfully!");
      navigate("/dashboard/rooms");
    } catch (error) {
      logger("error", "Error updating room:", error);
      toast.error("Failed to update meeting");
    }
  };
  return (
    <div className="rounded-xl border border-gray-600 p-8 primary-card-background">
      {/* Meeting Name and Your Name */}
      <h1 className="mb-3 text-white text-2xl font-bold">Edit Room</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Meeting Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={meetingName}
            onChange={(e) => setMeetingName(e.target.value)}
            placeholder="Enter meeting name"
            maxLength={25}
            className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
              meetingName.trim() && meetingName.trim().length < 3
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-600 focus:ring-[#6A1FBF]"
            }`}
            disabled={user?.plan !== "Business"}
          />
          <p className="text-gray-400 text-xs mt-1">
            {meetingName.trim() && meetingName.trim().length < 3 ? (
              <span className="text-red-400">
                Minimum 3 characters required
              </span>
            ) : (
              "25 characters max"
            )}
          </p>
        </div>
      </div>

      {/* Element Presets */}
      {/* <div className="mb-8">
        <h3 className="text-white text-lg font-semibold mb-4">
          Element Presets
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {elementPresets.map((preset, index) => (
            <div
              key={preset.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`relative bg-gray-700 rounded-lg p-4 border border-gray-600 cursor-move transition-all duration-200 ${
                draggedItem === index
                  ? "opacity-50 scale-95"
                  : dragOverIndex === index
                  ? "border-[#6A1FBF] bg-[#6A1FBF]/20"
                  : "hover:bg-gray-600"
              }`}
            >
              <button
                onClick={() => removeElementPreset(preset.id)}
                className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors z-10"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-white text-black rounded-lg flex items-center justify-center mb-2">
                  {getWidgetIcon(preset.type)}
                </div>
                <span className="text-white text-sm text-center">
                  {preset.name}
                </span>
              </div>
            </div>
          ))}
          <button
            onClick={addElementPreset}
            className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:bg-gray-600 transition-colors flex flex-col items-center justify-center"
          >
            <div className="w-12 h-12 border-2 border-dashed border-gray-400 rounded-lg flex items-center justify-center mb-2">
              <span className="text-2xl text-gray-400">+</span>
            </div>
            <span className="text-gray-400 text-sm">Add Preset</span>
          </button>
        </div>
      </div> */}

      {/* Co-Host */}
      {user?.plan == "Business" && (
        <div className="mb-8">
          <h3 className="text-white text-lg font-semibold mb-4">Co-Host</h3>
          <div className="relative">
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex-1 relative">
                <input
                  type="email"
                  value={invitesEmailInput}
                  onChange={(e) => setInvitesEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (
                        selectedSuggestionIndex >= 0 &&
                        suggestions[selectedSuggestionIndex]
                      ) {
                        e.preventDefault();
                        const selectedEmail =
                          suggestions[selectedSuggestionIndex];
                        handleAddCoHost(selectedEmail, null);
                        setSelectedSuggestionIndex(-1);
                      } else if (invitesEmailInput.trim() && !checkingUser) {
                        e.preventDefault();
                        const email = invitesEmailInput.trim();
                        handleAddCoHost(email, userExists === false);
                      }
                    } else if (
                      e.key === "ArrowDown" &&
                      suggestions.length > 0
                    ) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      setSelectedSuggestionIndex((prev) =>
                        prev < suggestions.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === "ArrowUp" && suggestions.length > 0) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      setSelectedSuggestionIndex((prev) =>
                        prev > 0 ? prev - 1 : 0
                      );
                    } else if (e.key === "Escape") {
                      setShowSuggestions(false);
                      setSelectedSuggestionIndex(-1);
                    }
                  }}
                  onFocus={() => {
                    if (invitesEmailInput.trim().length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  placeholder="Add co-host email and press Enter"
                  className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {/* User Status Indicator */}
                {invitesEmailInput.trim() && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingUser ? (
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : userExists === true ? (
                      <div className="text-green-500 text-sm">✓</div>
                    ) : userExists === false ? (
                      <Mail className="w-5 h-5 text-orange-500" />
                    ) : null}
                  </div>
                )}
                {/* Suggestions Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          handleAddCoHost(suggestion, null);
                        }}
                        className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                          selectedSuggestionIndex === index
                            ? "bg-indigo-100"
                            : ""
                        }`}
                      >
                        <div className="text-sm text-gray-700">
                          {suggestion}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* User Exists/Invite Options */}
                {!showSuggestions &&
                  invitesEmailInput.trim() &&
                  userExists !== null &&
                  !checkingUser && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {userExists ? (
                        <div
                          onClick={() =>
                            handleAddCoHost(invitesEmailInput.trim(), false)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b border-gray-200"
                        >
                          <div className="flex items-center gap-2">
                            <div className="text-green-500">✓</div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {invitesEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User exists - Add as Co-Host
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            handleAddCoHost(invitesEmailInput.trim(), true)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-200"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-orange-500" />
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {invitesEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User not found - Send Email Invite
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
              <button
                onClick={() => {
                  if (invitesEmailInput.trim()) {
                    const email = invitesEmailInput.trim();
                    if (userExists === false) {
                      handleAddCoHost(email, true);
                    } else {
                      handleAddCoHost(email, false);
                    }
                  }
                }}
                disabled={checkingUser}
                className="px-4 py-3 bg-[#6A1FBF] text-white rounded-lg hover:bg-[#6A1FBF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingUser ? "..." : "Add"}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {invitesList.map((email, index) => {
              const isInvite = inviteEmails.includes(email);
              return (
                <span
                  key={index}
                  className={`flex items-center px-3 py-1 rounded-full text-sm ${
                    isInvite
                      ? "bg-orange-600 text-white"
                      : "bg-gray-700 text-white"
                  }`}
                >
                  {isInvite && <Mail className="w-3 h-3 mr-1" />}
                  {email}
                  <button
                    onClick={() => {
                      setInvitesList((prev) =>
                        prev.filter((item) => item !== email)
                      );
                      setInviteEmails((prev) =>
                        prev.filter((item) => item !== email)
                      );
                    }}
                    className="ml-2 text-gray-400 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Speakers */}
      <div className="mb-8">
        <h3 className="text-white text-lg font-semibold mb-4">Speakers</h3>
        <div className="relative">
          <div className="flex items-center space-x-2 mb-4">
            <div className="flex-1 relative">
              <input
                type="email"
                value={speakersEmailInput}
                onChange={(e) => setSpeakersEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (
                      selectedSpeakerSuggestionIndex >= 0 &&
                      speakerSuggestions[selectedSpeakerSuggestionIndex]
                    ) {
                      e.preventDefault();
                      const selectedEmail =
                        speakerSuggestions[selectedSpeakerSuggestionIndex];
                      handleAddSpeaker(selectedEmail, null);
                      setSelectedSpeakerSuggestionIndex(-1);
                    } else if (
                      speakersEmailInput.trim() &&
                      !checkingSpeakerUser
                    ) {
                      e.preventDefault();
                      const email = speakersEmailInput.trim();
                      handleAddSpeaker(email, speakerUserExists === false);
                    }
                  } else if (
                    e.key === "ArrowDown" &&
                    speakerSuggestions.length > 0
                  ) {
                    e.preventDefault();
                    setShowSpeakerSuggestions(true);
                    setSelectedSpeakerSuggestionIndex((prev) =>
                      prev < speakerSuggestions.length - 1 ? prev + 1 : prev
                    );
                  } else if (
                    e.key === "ArrowUp" &&
                    speakerSuggestions.length > 0
                  ) {
                    e.preventDefault();
                    setShowSpeakerSuggestions(true);
                    setSelectedSpeakerSuggestionIndex((prev) =>
                      prev > 0 ? prev - 1 : 0
                    );
                  } else if (e.key === "Escape") {
                    setShowSpeakerSuggestions(false);
                    setSelectedSpeakerSuggestionIndex(-1);
                  }
                }}
                onFocus={() => {
                  if (speakersEmailInput.trim().length > 0) {
                    setShowSpeakerSuggestions(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowSpeakerSuggestions(false), 200);
                }}
                placeholder="Add speaker email and press Enter"
                className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {/* Speaker User Status Indicator */}
              {speakersEmailInput.trim() && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingSpeakerUser ? (
                    <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : speakerUserExists === true ? (
                    <div className="text-green-500 text-sm">✓</div>
                  ) : speakerUserExists === false ? (
                    <Mail className="w-5 h-5 text-orange-500" />
                  ) : null}
                </div>
              )}
              {/* Speaker Suggestions Dropdown */}
              {showSpeakerSuggestions && speakerSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {speakerSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        handleAddSpeaker(suggestion, null);
                      }}
                      className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                        selectedSpeakerSuggestionIndex === index
                          ? "bg-indigo-100"
                          : ""
                      }`}
                    >
                      <div className="text-sm text-gray-700">{suggestion}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* Speaker User Exists/Invite Options */}
              {!showSpeakerSuggestions &&
                speakersEmailInput.trim() &&
                speakerUserExists !== null &&
                !checkingSpeakerUser && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    {speakerUserExists ? (
                      <div
                        onClick={() =>
                          handleAddSpeaker(speakersEmailInput.trim(), false)
                        }
                        className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <div className="text-green-500">✓</div>
                          <div>
                            <div className="text-sm font-medium text-gray-700">
                              {speakersEmailInput.trim()}
                            </div>
                            <div className="text-xs text-gray-500">
                              User exists - Add as Speaker
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() =>
                          handleAddSpeaker(speakersEmailInput.trim(), true)
                        }
                        className="px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <Mail className="w-5 h-5 text-orange-500" />
                          <div>
                            <div className="text-sm font-medium text-gray-700">
                              {speakersEmailInput.trim()}
                            </div>
                            <div className="text-xs text-gray-500">
                              User not found - Send Email Invite
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>
            <button
              onClick={() => {
                if (speakersEmailInput.trim()) {
                  const email = speakersEmailInput.trim();
                  if (speakerUserExists === false) {
                    handleAddSpeaker(email, true);
                  } else {
                    handleAddSpeaker(email, false);
                  }
                }
              }}
              disabled={checkingSpeakerUser}
              className="px-4 py-3 bg-[#6A1FBF] text-white rounded-lg hover:bg-[#6A1FBF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checkingSpeakerUser ? "..." : "Add"}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {speakersList.map((email, index) => {
            const isInvite = speakerInviteEmails.includes(email);
            return (
              <span
                key={index}
                className={`flex items-center px-3 py-1 rounded-full text-sm ${
                  isInvite
                    ? "bg-orange-600 text-white"
                    : "bg-gray-700 text-white"
                }`}
              >
                {isInvite && <Mail className="w-3 h-3 mr-1" />}
                {email}
                <button
                  onClick={() => {
                    setSpeakersList((prev) =>
                      prev.filter((item) => item !== email)
                    );
                    setSpeakerInviteEmails((prev) =>
                      prev.filter((item) => item !== email)
                    );
                  }}
                  className="ml-2 text-gray-400 hover:text-white"
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
        </div>
      </div>

      {/* Room Settings */}
      <div className="mb-8">
        <h3 className="text-white text-lg font-semibold mb-6">ROOM SETTINGS</h3>
        <div className="space-y-6">
          {/* Allow Chat */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Chat</h4>
              <p className="text-gray-400 text-sm">
                (Enable or disable in-meeting chat for participants.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("allowChat", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowChat
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("allowChat", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowChat
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Allow Screen Sharing */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Screen Sharing</h4>
              <p className="text-gray-400 text-sm">
                (Let participants share their screen during the meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("allowScreenSharing", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowScreenSharing === false
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Host only
              </button>
              <button
                onClick={() => handleSettingChange("allowScreenSharing", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowScreenSharing === true
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Everyone
              </button>
            </div>
          </div>

          {/* Mute participants on entry */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">
                Mute participants on entry
              </h4>
              <p className="text-gray-400 text-sm">
                (Hosts can unmute participants later.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() =>
                  handleSettingChange("muteParticipantsOnEntry", false)
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.muteParticipantsOnEntry
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() =>
                  handleSettingChange("muteParticipantsOnEntry", true)
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.muteParticipantsOnEntry
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Allow Reactions */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Reactions</h4>
              <p className="text-gray-400 text-sm">
                (Participants can use emoji reactions during the meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("allowReactions", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowReactions
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("allowReactions", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowReactions
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Allow Raise Hand */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Raise Hand</h4>
              <p className="text-gray-400 text-sm">
                (Participants can raise their hand to request attention from the
                host.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("allowRaiseHand", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowRaiseHand
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("allowRaiseHand", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowRaiseHand
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Allow Participant Ranking */}
          {/* <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Participant Ranking</h4>
              <p className="text-gray-400 text-sm">
                (Enable participants to sort or rank themselves in the meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("allowParticipantRanking", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowParticipantRanking
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("allowParticipantRanking", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowParticipantRanking
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div> */}

          {/* Guest can Unmute */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Guest can Unmute</h4>
              <p className="text-gray-400 text-sm">
                (Allow guests to unmute themselves during the meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("guestCanUnmute", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.guestCanUnmute
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("guestCanUnmute", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.guestCanUnmute
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Require Password */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Require Password</h4>
              <p className="text-gray-400 text-sm">
                (Participants will need to enter a password to join the
                meeting.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("requirePassword", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.requirePassword
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
              <button
                onClick={() => handleSettingChange("requirePassword", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.requirePassword
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
            </div>
          </div>

          {/* Password Input */}
          {roomSettings.requirePassword && (
            <div className="pl-8">
              <label className="block text-white text-sm font-medium mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2 mb-2">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    ref={(el) => (passwordInputRefs.current[index] = el)}
                    type="tel"
                    value={roomSettings.password[index] || ""}
                    onChange={(e) => handlePasswordDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handlePasswordKeyDown(index, e)}
                    onPaste={handlePasswordPaste}
                    maxLength={1}
                    required
                    className={`w-14 h-14 text-center text-xl font-semibold bg-white border-2 rounded-lg text-black focus:outline-none focus:ring-2 transition-colors ${
                      roomSettings.password.length > 0 && roomSettings.password.length !== 4
                        ? "border-red-500 focus:ring-red-500"
                        : roomSettings.password.length === 4
                        ? "border-green-500 focus:ring-green-500"
                        : "border-gray-600 focus:ring-[#6A1FBF]"
                    }`}
                  />
                ))}
              </div>
              <p className="text-gray-400 text-xs">
                {roomSettings.password.length > 0 && roomSettings.password.length !== 4 ? (
                  <span className="text-red-400">
                    Password must be exactly 4 digits
                  </span>
                ) : (
                  "4 digits, numbers only"
                )}
              </p>
            </div>
          )}

          {/* Host Approval Required */}
          {user?.plan == "Business" && (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-white font-medium">Waiting Room</h4>
                <p className="text-gray-400 text-sm">
                  (Participants will be placed in a waiting room before joining
                  the meeting.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() =>
                    handleSettingChange("hostApprovalRequired", false)
                  }
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !roomSettings.hostApprovalRequired
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
                <button
                  onClick={() =>
                    handleSettingChange("hostApprovalRequired", true)
                  }
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    roomSettings.hostApprovalRequired
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
              </div>
            </div>
          )}

          {/* Exit URL */}
          {user?.plan == "Business" && (
            <div className="border-t border-gray-600 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Exit URL</h4>
                  <p className="text-gray-400 text-sm">
                    (Redirect participants to a specific URL when they leave the
                    meeting.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      setExitUrlEnabled(false);
                      setExitUrl("");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !exitUrlEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                  <button
                    onClick={() => setExitUrlEnabled(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      exitUrlEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                </div>
              </div>

              {exitUrlEnabled && (
                <div className="pl-4">
                  <label className="block text-white text-sm font-medium mb-2">
                    Exit URL
                  </label>
                  <input
                    type="url"
                    value={typeof exitUrl === "string" ? exitUrl : ""}
                    onChange={(e) => setExitUrl(e.target.value)}
                    placeholder="https://example.com"
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      exitUrl &&
                      typeof exitUrl === "string" &&
                      exitUrl.trim() &&
                      !isValidUrl(exitUrl.trim())
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-600 focus:ring-[#6A1FBF]"
                    }`}
                  />
                  {exitUrl &&
                    typeof exitUrl === "string" &&
                    exitUrl.trim() &&
                    !isValidUrl(exitUrl.trim()) && (
                      <p className="text-red-400 text-xs mt-1">
                        Please enter a valid URL (e.g., https://example.com)
                      </p>
                    )}
                  {exitUrl &&
                    typeof exitUrl === "string" &&
                    exitUrl.trim() &&
                    isValidUrl(exitUrl.trim()) && (
                      <p className="text-green-400 text-xs mt-1">Valid URL ✓</p>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Custom Button */}
          {user?.plan == "Business" && (
            <div className="border-t border-gray-600 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Custom Button</h4>
                  <p className="text-gray-400 text-sm">
                    (Add a custom button with a link in the meeting room.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      setCustomButtonEnabled(false);
                      setCustomButtonText("");
                      setCustomButtonUrl("");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !customButtonEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                  <button
                    onClick={() => setCustomButtonEnabled(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      customButtonEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                </div>
              </div>

              {customButtonEnabled && (
                <div className="pl-4 space-y-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Button Name
                    </label>
                    <input
                      type="text"
                      value={customButtonText}
                      onChange={(e) => setCustomButtonText(e.target.value)}
                      placeholder="e.g., Visit Our Website"
                      maxLength={50}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        customButtonText.trim() &&
                        customButtonText.trim().length < 3
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {customButtonText.trim() &&
                      customButtonText.trim().length < 3 ? (
                        <span className="text-red-400">
                          Minimum 3 characters required
                        </span>
                      ) : (
                        `${customButtonText.length}/50 characters`
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Button URL
                    </label>
                    <input
                      type="url"
                      value={customButtonUrl}
                      onChange={(e) => setCustomButtonUrl(e.target.value)}
                      placeholder="https://example.com"
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        customButtonUrl.trim() &&
                        !isValidUrl(customButtonUrl.trim())
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    {customButtonUrl.trim() &&
                      !isValidUrl(customButtonUrl.trim()) && (
                        <p className="text-red-400 text-xs mt-1">
                          Please enter a valid URL (e.g., https://example.com)
                        </p>
                      )}
                    {customButtonUrl.trim() &&
                      isValidUrl(customButtonUrl.trim()) && (
                        <p className="text-green-400 text-xs mt-1">
                          Valid URL ✓
                        </p>
                      )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Waiting Room Text */}
          {user?.plan == "Business" && (
            <div className="border-t border-gray-600 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Waiting Room Text</h4>
                  <p className="text-gray-400 text-sm">
                    (Customize the messages shown to participants in the waiting
                    room.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      setWaitingRoomTextEnabled(false);
                      setWaitingRoomTitle("");
                      setWaitingRoomMessage("");
                      setWaitingRoomSubText("");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !waitingRoomTextEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                  <button
                    onClick={() => setWaitingRoomTextEnabled(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      waitingRoomTextEnabled
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                </div>
              </div>

              {waitingRoomTextEnabled && (
                <div className="pl-4 space-y-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={waitingRoomTitle}
                      onChange={(e) => setWaitingRoomTitle(e.target.value)}
                      placeholder="e.g., Waiting for Host to Approve"
                      maxLength={100}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        waitingRoomTitle.trim() &&
                        waitingRoomTitle.trim().length < 3
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {waitingRoomTitle.trim() &&
                      waitingRoomTitle.trim().length < 3 ? (
                        <span className="text-red-400">
                          Minimum 3 characters required
                        </span>
                      ) : (
                        `${waitingRoomTitle.length}/100 characters`
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={waitingRoomMessage}
                      onChange={(e) => setWaitingRoomMessage(e.target.value)}
                      placeholder="e.g., Please wait while the host reviews your request to join"
                      maxLength={200}
                      rows={2}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 resize-none ${
                        waitingRoomMessage.trim() &&
                        waitingRoomMessage.trim().length < 3
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {waitingRoomMessage.trim() &&
                      waitingRoomMessage.trim().length < 3 ? (
                        <span className="text-red-400">
                          Minimum 3 characters required
                        </span>
                      ) : (
                        `${waitingRoomMessage.length}/200 characters`
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Sub Text <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={waitingRoomSubText}
                      onChange={(e) => setWaitingRoomSubText(e.target.value)}
                      placeholder="e.g., You will be automatically admitted once approved"
                      maxLength={150}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        waitingRoomSubText.trim() &&
                        waitingRoomSubText.trim().length < 3
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      {waitingRoomSubText.trim() &&
                      waitingRoomSubText.trim().length < 3 ? (
                        <span className="text-red-400">
                          Minimum 3 characters required
                        </span>
                      ) : (
                        `${waitingRoomSubText.length}/150 characters`
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Custom Background */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-white font-medium">Custom Background</h4>
                <p className="text-gray-400 text-sm">
                  (Add a custom background to your meeting room.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    setCustomBackgroundEnabled(false);
                    setBackgroundImage(null);
                    setBackgroundImagePreview(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !customBackgroundEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
                <button
                  onClick={() => setCustomBackgroundEnabled(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    customBackgroundEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
              </div>
            </div>

            {customBackgroundEnabled && (
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setBackgroundType("solid")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "solid"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Solid Color
                  </button>
                  <button
                    onClick={() => setBackgroundType("gradient")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "gradient"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Gradient
                  </button>
                  <button
                    onClick={() => setBackgroundType("image")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "image"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Image
                  </button>
                </div>

                {/* Solid Color Picker */}
                {backgroundType === "solid" && (
                  <div className="pl-4">
                    <label className="block text-white text-sm font-medium mb-2">
                      Background Color
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        placeholder="#1a1a2e"
                        className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
                      />
                      <div
                        className="w-20 h-10 rounded border border-gray-600"
                        style={{ backgroundColor }}
                      />
                    </div>
                  </div>
                )}

                {/* Gradient Color Picker */}
                {backgroundType === "gradient" && (
                  <div className="pl-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        First Color
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={gradientColor1}
                          onChange={(e) => setGradientColor1(e.target.value)}
                          className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={gradientColor1}
                          onChange={(e) => setGradientColor1(e.target.value)}
                          placeholder="#1a1a2e"
                          className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Second Color
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={gradientColor2}
                          onChange={(e) => setGradientColor2(e.target.value)}
                          className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={gradientColor2}
                          onChange={(e) => setGradientColor2(e.target.value)}
                          placeholder="#615fff"
                          className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Gradient Direction
                      </label>
                      <select
                        value={gradientDirection}
                        onChange={(e) => setGradientDirection(e.target.value)}
                        className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
                      >
                        <option value="to right">Left to Right</option>
                        <option value="to left">Right to Left</option>
                        <option value="to bottom">Top to Bottom</option>
                        <option value="to top">Bottom to Top</option>
                        <option value="to bottom right">Diagonal ↘</option>
                        <option value="to bottom left">Diagonal ↙</option>
                        <option value="to top right">Diagonal ↗</option>
                        <option value="to top left">Diagonal ↖</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Preview
                      </label>
                      <div
                        className="w-full h-20 rounded border border-gray-600"
                        style={{
                          background: `linear-gradient(${gradientDirection}, ${gradientColor1}, ${gradientColor2})`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Image Upload */}
                {backgroundType === "image" && (
                  <div className="pl-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Background Image
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const maxSize = 5 * 1024 * 1024;
                            if (file.size > maxSize) {
                              toast.error("Image size should not exceed 5MB");
                              e.target.value = "";
                              return;
                            }
                            setBackgroundImage(file);
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setBackgroundImagePreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="block w-full text-sm text-gray-400
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-[#6A1FBF] file:text-white
                        hover:file:bg-[#7B2FCF]
                        file:cursor-pointer cursor-pointer"
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        Maximum file size: 5MB
                      </p>
                      {backgroundImage && (
                        <p className="text-green-400 text-xs mt-1">
                          Selected: {backgroundImage.name} (
                          {(backgroundImage.size / 1024).toFixed(2)} KB)
                        </p>
                      )}
                    </div>

                    {backgroundImagePreview && (
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Preview
                        </label>
                        <div className="relative w-full h-64 rounded-lg overflow-hidden border border-gray-600 bg-gray-800">
                          <img
                            src={backgroundImagePreview}
                            alt="Background preview"
                            className="w-full h-full object-contain"
                          />
                          <button
                            onClick={() => {
                              setBackgroundImage(null);
                              setBackgroundImagePreview(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                            className="absolute top-2 right-2 p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1"
                          >
                            <X size={16} />
                            <span className="text-sm">Remove</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Start Meeting Button */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleUpdateMeeting}
          className="bg-[#6A1FBF] text-white font-bold py-3 px-8 rounded-lg hover:bg-[#7B2FCF] transition-colors"
        >
          Update Meeting
        </button>
      </div>
    </div>
  );
}

export default EditRoom;
